import './App.css';
import './components/Feed.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import SignUp from './components/SignUp';
import UserProfile from './components/UserProfile';
import SpotifySearch from './components/SpotifySearch';
import Feed from './components/Feed';
import { useState, useEffect } from 'react';
import { playlistsService } from './services/playlistsService';
import { playlistPermissionsService } from './services/playlistPermissionsService';

function AppContent() {
  const { user } = useAuth();
  const [showLogin, setShowLogin] = useState(true);

  // Estado para navegación principal - 'home', 'search', 'profile'
  const [currentView, setCurrentView] = useState('home');

  // Estado global de playlists
  const [showPlaylistModal, setShowPlaylistModal] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  const [newPlaylist, setNewPlaylist] = useState({
    name: "",
    image: "",
    description: "",
    isPublic: true,
  });
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);

  // ⚡ Cargar playlists del usuario desde la BD cuando se autentica
  useEffect(() => {
    const loadPlaylists = async () => {
      if (user?.id) {
        try {
          setLoadingPlaylists(true);

          // Cargar playlists propias
          const userPlaylists = await playlistsService.getUserPlaylists(user.id);

          // Cargar playlists compartidas con permiso de colaboración
          const sharedPlaylists = await playlistPermissionsService.getSharedWithUser(user.id);
          const collaborativePlaylists = sharedPlaylists.filter(
            p => p.permission === 'collaborate'
          );

          // Combinar ambas listas
          setPlaylists([...userPlaylists, ...collaborativePlaylists]);
        } catch (error) {
          console.error('Error cargando playlists:', error);
        } finally {
          setLoadingPlaylists(false);
        }
      } else {
        setPlaylists([]);
      }
    };

    loadPlaylists();
  }, [user]);

  // Función para abrir modal de playlist
  const openPlaylistModal = (song = null) => {
    console.log("🎵 Abriendo modal con:", song);
    setSelectedSong(song);
    setShowPlaylistModal(true);
  };

  // Función para cerrar modal de playlist
  const closePlaylistModal = () => {
    console.log("❌ Cerrando modal");
    setShowPlaylistModal(false);
    setSelectedSong(null);
    setSelectedPlaylist(null);
    setNewPlaylist({ name: "", image: "", description: "", isPublic: true });
  };

  // Función para crear playlist (AHORA GUARDA EN BD)
  const handleCreatePlaylist = async () => {
    if (!newPlaylist.name.trim()) {
      alert("Debes poner un nombre a la playlist");
      return;
    }

    if (!user?.id) {
      alert("Debes estar autenticado para crear una playlist");
      return;
    }

    try {
      // Crear playlist en la BD
      const createdPlaylist = await playlistsService.createPlaylist(user.id, newPlaylist);

      // Si hay una canción seleccionada, agregarla a la playlist
      if (selectedSong) {
        console.log('➕ Agregando canción desde modal:', {
          title: selectedSong.title,
          artist: selectedSong.artist,
          hasPreview: !!selectedSong.previewUrl,
          previewUrl: selectedSong.previewUrl
        });
        await playlistsService.addTrackToPlaylist(createdPlaylist.id, selectedSong);
      }

      // Recargar playlists desde la BD
      const userPlaylists = await playlistsService.getUserPlaylists(user.id);
      const sharedPlaylists = await playlistPermissionsService.getSharedWithUser(user.id);
      const collaborativePlaylists = sharedPlaylists.filter(p => p.permission === 'collaborate');
      setPlaylists([...userPlaylists, ...collaborativePlaylists]);

      setNewPlaylist({ name: "", image: "", description: "", isPublic: true });

      if (selectedSong) {
        alert(`✅ Canción "${selectedSong.title}" agregada a "${newPlaylist.name}"`);
      } else {
        alert(`✅ Playlist "${newPlaylist.name}" creada`);
      }

      closePlaylistModal();
    } catch (error) {
      console.error('Error creando playlist:', error);
      alert('Error al crear la playlist. Por favor intenta de nuevo.');
    }
  };

  // Función para agregar canción a playlist existente (AHORA GUARDA EN BD)
  const handleAddSongToPlaylist = async () => {
    if (!selectedPlaylist || !selectedSong) {
      alert("Selecciona una playlist primero");
      return;
    }

    if (!user?.id) {
      alert("Debes estar autenticado");
      return;
    }

    try {
      console.log('➕ Agregando a playlist existente:', {
        title: selectedSong.title,
        artist: selectedSong.artist,
        hasPreview: !!selectedSong.previewUrl,
        previewUrl: selectedSong.previewUrl
      });

      // Verificar si la canción ya está en la playlist
      const exists = await playlistsService.isTrackInPlaylist(
        selectedPlaylist.id,
        selectedSong.title,
        selectedSong.artist
      );

      if (exists) {
        alert("Esta canción ya está en la playlist");
        return;
      }

      // Agregar canción a la playlist en la BD
      await playlistsService.addTrackToPlaylist(selectedPlaylist.id, selectedSong);

      // Recargar playlists desde la BD
      const userPlaylists = await playlistsService.getUserPlaylists(user.id);
      const sharedPlaylists = await playlistPermissionsService.getSharedWithUser(user.id);
      const collaborativePlaylists = sharedPlaylists.filter(p => p.permission === 'collaborate');
      setPlaylists([...userPlaylists, ...collaborativePlaylists]);

      alert(`✅ Canción agregada a "${selectedPlaylist.name}"`);
      closePlaylistModal();
    } catch (error) {
      console.error('Error agregando canción:', error);
      alert('Error al agregar la canción. Por favor intenta de nuevo.');
    }
  };

  if (!user) {
    return (
      <div className="App">
        {showLogin ? (
          <Login onToggleForm={() => setShowLogin(false)} />
        ) : (
          <SignUp onToggleForm={() => setShowLogin(true)} />
        )}
      </div>
    );
  }

  return (
    <div className="App" style={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: '#121212', color: 'white' }}>
      {/* Contenido principal */}
      <div style={{
        width: '100%',
        maxWidth: '1400px',
        display: 'flex',
        justifyContent: 'center',
        flexGrow: 1
      }}>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Vista Home - Feed con funcionalidad de playlists y lyrics */}
          {currentView === 'home' && (
            <Feed
              onNavigate={setCurrentView}
              playlists={playlists}
              setPlaylists={setPlaylists}
              openPlaylistModal={openPlaylistModal}
            />
          )}

          {/* Vista Search - Búsqueda de Spotify con playlists */}
          {currentView === 'search' && (
            <SpotifySearch
              onNavigate={setCurrentView}
              openPlaylistModal={openPlaylistModal}
            />
          )}

          {/* Vista Profile - Perfil de usuario con playlists */}
          {currentView === 'profile' && (
            <UserProfile
              onNavigate={setCurrentView}
              openPlaylistModal={openPlaylistModal}
            />
          )}
        </div>
      </div>

      {/* Modal de Playlist global */}
      {showPlaylistModal && (
        <div
          className="playlist-modal-overlay"
          onClick={closePlaylistModal}
        >
          <div
            className="playlist-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>
              {selectedSong
                ? `Agregar "${selectedSong.title}" a Playlist`
                : "Crear Nueva Playlist"}
            </h3>

            {selectedSong && (
              <div className="selected-song-info">
                <img
                  src={selectedSong.image}
                  alt={selectedSong.title}
                  className="playlist-song-image"
                />
                <div>
                  <strong>{selectedSong.title}</strong>
                  <p>{selectedSong.artist}</p>
                </div>
              </div>
            )}

            {/* Elegir playlist existente */}
            {selectedSong && playlists.length > 0 && (
              <div className="existing-playlist-section">
                <h4>Selecciona una playlist existente:</h4>
                <select
                  value={selectedPlaylist?.id || ""}
                  onChange={(e) => {
                    const playlistId = e.target.value;
                    setSelectedPlaylist(
                      playlists.find((p) => p.id === playlistId)
                    );
                  }}
                >
                  <option value="">--Selecciona--</option>
                  {playlists.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.isShared ? '🤝 ' : ''}{p.name} ({p.songs?.length || 0} canciones)
                    </option>
                  ))}
                </select>
                <button
                  className="action-button btn-add-to-playlist"
                  onClick={handleAddSongToPlaylist}
                  disabled={!selectedPlaylist}
                >
                  ➕ Agregar a esta playlist
                </button>
              </div>
            )}

            {/* Crear nueva playlist */}
            <div className="new-playlist-section">
              <h4>{selectedSong ? "O crea una nueva:" : "Nueva Playlist:"}</h4>
              <input
                type="text"
                placeholder="Nombre de la playlist"
                value={newPlaylist.name}
                onChange={(e) =>
                  setNewPlaylist({ ...newPlaylist, name: e.target.value })
                }
              />
              <input
                type="text"
                placeholder="URL de imagen (opcional)"
                value={newPlaylist.image}
                onChange={(e) =>
                  setNewPlaylist({ ...newPlaylist, image: e.target.value })
                }
              />
              <textarea
                placeholder="Descripción (opcional)"
                value={newPlaylist.description}
                onChange={(e) =>
                  setNewPlaylist({ ...newPlaylist, description: e.target.value })
                }
                rows="3"
              />
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={newPlaylist.isPublic}
                  onChange={(e) =>
                    setNewPlaylist({ ...newPlaylist, isPublic: e.target.checked })
                  }
                />
                Playlist pública
              </label>
              <button
                className="action-button btn-create-playlist"
                onClick={handleCreatePlaylist}
              >
                Crear playlist
              </button>
            </div>

            <button
              className="action-button btn-close-modal"
              onClick={closePlaylistModal}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
